import logo from "@/assets/logo-cineblocks-1.svg";
import { ProfileCard } from "@/components/onboarding/profile-card";
import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/context/authentication/auth-context";
import { useChangeRole } from "@/services/queries/useUsers";
import { Role } from "@/types/users";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";

export default function Onboarding() {
  const [selectedProfile, setSelectedProfile] = useState<Role | null>(null);
  const [_, navigate] = useLocation();

  const { person } = useAuthContext()
  const { mutateAsync: updateRole, isPending } = useChangeRole();

  // Auto-select Produtor role
  useEffect(() => {
    if (person?.user.id) {
      handleContinue(Role.produtor);
    }
  }, [person?.user.id]);

  const handleContinue = (role: Role = selectedProfile!) => {
    if (role && person?.user.id) {
      updateRole({ id: person.user.id, role: role }).then(() => {
        navigate('/cadastrar-pessoa');
      })
    }
  };

  // Show loading while auto-selecting/updating
  // This effectively bypasses the UI but keeps the code below accessible if this logic is removed.
  if (true || isPending) { // Force loading state for now to bypass UI
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0c1427]">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
        <span className="ml-2 text-white font-bold">Configurando perfil...</span>
      </div>
    )
  }

  return (
    <div className="min-h-screen lg:max-w-screen-2xl mx-auto flex flex-col justify-center items-center p-4">
      <div className="max-w-6xl w-full space-y-8">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src={logo} alt="Logo Cineblocks" className="w-8" />
            <h1 className="text-3xl font-bold text-white">cineblocks</h1>
          </div>
        </div>

        <div className="bg-darkblue rounded-lg px-10 py-14 flex flex-col items-center gap-8">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Bem-vindo ao Cineblocks, siga os passos
            </h2>
            <h2 className="text-2xl font-bold text-white mb-4">
              para finalizar o seu cadastro na plataforma.
            </h2>
            <p className="text-gray-300 text-sm">
              Selecione qual é o seu perfil antes de acessar e preencher seus dados.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ProfileCard
              type={Role.espectador}
              title="Espectador"
              subtitle="Tenho interesse em ficar ligado"
              description="Não realizo investimentos mas gosto de acompanhar as tendências e interagir com as novidades do mercado."
              isSelected={selectedProfile === Role.espectador}
              onSelect={() => setSelectedProfile(Role.espectador)}
              className="bg-purple-500 hover:bg-purple-700"
            />

            <ProfileCard
              type={Role.investidor}
              title="Investidor"
              subtitle="Tenho interesse em investir"
              description="Quero realizar investimentos em meus filmes e séries preferidas, vender ativos digitais ou captar recursos."
              isSelected={selectedProfile === Role.investidor}
              onSelect={() => setSelectedProfile(Role.investidor)}
              className="bg-gold-600 hover:bg-gold-700"
            />

            <ProfileCard
              type={Role.produtor}
              title="Produtor"
              subtitle="Faço conteúdo e busco investimento"
              description="Sou produtor de filmes, séries, curtas e vídeos. Busco investimentos para minhas produções."
              isSelected={selectedProfile === Role.produtor}
              onSelect={() => setSelectedProfile(Role.produtor)}
              className="bg-poolgreen-800 hover:bg-poolgreen-900"
            />

            <ProfileCard
              type={Role.prestador_servico}
              title="Prestador de Serviços"
              subtitle="Trabalho com serviços audiovisuais"
              description="Sou prestador de serviços e quero cadastrar meu CPF e CNPJ vinculado ao Cineblocks."
              isSelected={selectedProfile === Role.prestador_servico}
              onSelect={() => setSelectedProfile(Role.prestador_servico)}
              className="bg-blue-900 hover:bg-blue-950"
            />
          </div>

          <Button
            type="button"
            onClick={() => handleContinue()}
            disabled={!selectedProfile}
            className="w-72"
            children={isPending ? <Loader2 className="animate-spin" /> : "Acessar"}
          />
        </div>
      </div>
    </div>
  );
}